import { Container, Sprite } from 'pixi.js';
import { BUILDING_DEFS, colOf, xOf, yOf } from '../sim/constants';
import type { Building, GoodKind, SimState } from '../sim/types';
import type { GrayboxTextures } from './graybox';
import { ELEV_STEP, HALF_H, tileTopY, vertexX, vertexY } from './iso';

const GOODS_TINTS: Record<GoodKind, number> = {
  corn: 0xe8c170,
  wheat: 0xe4d5b0,
  cattle: 0x8a6a4a,
  glass: 0x9ac8d8,
  chips: 0x8a95c8,
};

interface Accessory {
  kind: 'sign' | 'exhaust' | 'drip';
  sprites: Sprite[];
  baseX: number;
  baseY: number;
  seed: number;
}

/** Buildings, their animated accessories, and ore piles. */
export class EntityLayer {
  readonly container = new Container();
  private readonly tex: GrayboxTextures;
  private readonly buildingSprites = new Map<number, Sprite>();
  private readonly accessories = new Map<number, Accessory>();
  private readonly pileSprites = new Map<number, Sprite>();
  private readonly goodsPileSprites = new Map<number, Sprite>();

  constructor(tex: GrayboxTextures) {
    this.tex = tex;
    this.container.sortableChildren = true;
  }

  syncBuildings(state: SimState): void {
    const seen = new Set<number>();
    for (const building of state.buildings) {
      seen.add(building.id);
      if (this.buildingSprites.has(building.id)) continue;
      this.addBuilding(building);
    }
    for (const [id, sprite] of this.buildingSprites) {
      if (!seen.has(id)) {
        sprite.destroy();
        this.buildingSprites.delete(id);
        const accessory = this.accessories.get(id);
        if (accessory) {
          for (const extra of accessory.sprites) extra.destroy();
          this.accessories.delete(id);
        }
      }
    }
  }

  private addBuilding(building: Building): void {
    const def = BUILDING_DEFS[building.type];
    const entry = this.tex.building(building.type);
    const vx = vertexX(building.x, building.y);
    const vy = vertexY(building.x, building.y);
    const sprite = new Sprite(entry.texture);
    sprite.position.set(vx + entry.offsetX, vy + entry.offsetY);
    const zIndex = (building.x + def.w - 1 + building.y + def.h - 1) * 16 + 8;
    sprite.zIndex = zIndex;
    sprite.cullable = true;
    this.buildingSprites.set(building.id, sprite);
    this.container.addChild(sprite);

    if (entry.anim) {
      const kind = entry.anim.kind === 'sign' ? 'sign' : entry.anim.kind === 'exhaust' ? 'exhaust' : 'drip';
      const count = kind === 'exhaust' ? 3 : 1;
      const sprites: Sprite[] = [];
      for (let index = 0; index < count; index += 1) {
        const extra = new Sprite(this.tex.accessory(kind === 'exhaust' ? 'puff' : kind));
        extra.zIndex = zIndex + 1;
        if (kind === 'sign') extra.anchor.set(0.3, 0);
        else extra.anchor.set(0.5, 0.5);
        extra.cullable = true;
        sprites.push(extra);
        this.container.addChild(extra);
      }
      this.accessories.set(building.id, {
        kind,
        sprites,
        baseX: vx + entry.anim.x,
        baseY: vy + entry.anim.y,
        seed: building.id % 7,
      });
    }
  }

  /** Small life on the sim-time clock: puffs rise, signs sway, tanks drip. */
  animate(simSeconds: number): void {
    for (const accessory of this.accessories.values()) {
      if (accessory.kind === 'exhaust') {
        for (let index = 0; index < accessory.sprites.length; index += 1) {
          const sprite = accessory.sprites[index]!;
          const t = (simSeconds * 0.45 + index / 3 + accessory.seed * 0.13) % 1;
          sprite.position.set(accessory.baseX + Math.sin((t + index) * 9) * 1.5, accessory.baseY - t * 16);
          sprite.alpha = t < 0.08 ? t * 6 : (1 - t) * 0.55;
        }
      } else if (accessory.kind === 'sign') {
        const sprite = accessory.sprites[0]!;
        sprite.position.set(accessory.baseX, accessory.baseY);
        sprite.rotation = Math.sin(simSeconds * 1.7 + accessory.seed) * 0.14;
      } else {
        const sprite = accessory.sprites[0]!;
        const t = (simSeconds * 0.3 + accessory.seed * 0.29) % 1;
        sprite.visible = t < 0.3;
        sprite.position.set(accessory.baseX, accessory.baseY + t * 40);
        sprite.alpha = 0.9 - t * 2;
      }
    }
  }

  syncPiles(state: SimState): void {
    const seen = new Set<number>();
    for (const [col, pile] of state.piles) {
      seen.add(col);
      let sprite = this.pileSprites.get(col);
      if (!sprite) {
        sprite = new Sprite(this.tex.orePile);
        sprite.anchor.set(0.5, 1);
        this.pileSprites.set(col, sprite);
        this.container.addChild(sprite);
      }
      const x = xOf(col);
      const y = yOf(col);
      const depth = state.terrain.depth[colOf(x, y)] ?? 0;
      sprite.position.set((x - y) * 32, tileTopY(x, y, depth) + HALF_H + ELEV_STEP / 2);
      sprite.tint = pile.goldDt > pile.ironDt ? 0xf2c14e : 0x8a6f54;
      sprite.zIndex = (x + y) * 16 + 4;
    }
    for (const [col, sprite] of this.pileSprites) {
      if (!seen.has(col)) {
        sprite.destroy();
        this.pileSprites.delete(col);
      }
    }

    const goodsSeen = new Set<number>();
    for (const [col, pile] of state.goodsPiles) {
      goodsSeen.add(col);
      let sprite = this.goodsPileSprites.get(col);
      if (!sprite) {
        sprite = new Sprite(this.tex.goodsPile);
        sprite.anchor.set(0.5, 1);
        this.goodsPileSprites.set(col, sprite);
        this.container.addChild(sprite);
      }
      const x = xOf(col);
      const y = yOf(col);
      const depth = state.terrain.depth[colOf(x, y)] ?? 0;
      sprite.position.set((x - y) * 32, tileTopY(x, y, depth) + HALF_H + ELEV_STEP / 2);
      // Cattle get their own pen art — a brown-tinted grain sack reads as
      // grain, and the good changing at a col must swap the texture too.
      const isCattle = pile.good === 'cattle';
      sprite.texture = isCattle ? this.tex.cattlePile : this.tex.goodsPile;
      sprite.tint = isCattle ? 0xffffff : GOODS_TINTS[pile.good];
      sprite.zIndex = (x + y) * 16 + 4;
    }
    for (const [col, sprite] of this.goodsPileSprites) {
      if (!goodsSeen.has(col)) {
        sprite.destroy();
        this.goodsPileSprites.delete(col);
      }
    }
  }
}
